import React, { useEffect, useRef } from 'react';
import { Drawer, Button, Tag, Divider, Typography, Alert, Collapse } from 'antd';
import { GlobalOutlined, LinkedinOutlined } from '@ant-design/icons';
import { markOpportunityViewed } from '../../services/api';
import { RejectionWithOpportunity, LLMRating, RejectionReason } from '../../types';
import { renderBulletList } from '../../utils/renderUtils';
import SourceTag from '../common/SourceTag';
import css from './RejectedOpportunityDrawer.module.scss';

const { Title, Text, Paragraph } = Typography;

interface RejectedOpportunityDrawerProps {
  rejection: RejectionWithOpportunity | null;
  open: boolean;
  onClose: () => void;
}

const ratingConfig: Record<LLMRating, { label: string; color: string }> = {
  perfect_fit: { label: 'Perfect Fit', color: '#52c41a' },
  strong_alignment: { label: 'Strong Alignment', color: '#73d13d' },
  potential_candidate: { label: 'Potential Candidate', color: '#faad14' },
  partial_fit: { label: 'Partial Fit', color: '#fa8c16' },
  misalignment: { label: 'Misalignment', color: '#ff4d4f' },
};

const rejectionReasonConfig: Record<RejectionReason, { label: string; color: string }> = {
  not_interested: { label: 'Not Interested', color: 'default' },
  bad_fit: { label: 'Bad Fit', color: 'red' },
  company_concerns: { label: 'Company Concerns', color: 'orange' },
  salary_expectations: { label: 'Salary', color: 'gold' },
  location_mismatch: { label: 'Location', color: 'blue' },
  other: { label: 'Other', color: 'purple' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const RejectedOpportunityDrawer: React.FC<RejectedOpportunityDrawerProps> = ({
  rejection,
  open,
  onClose,
}) => {
  // Email read semantics carried into this tab. Rejecting from a row never opens
  // anything, so an opportunity passed on from the list is still UNREAD — and an
  // Undo has to hand it back exactly as it was, bold and unmissable, not filed
  // under "already seen". Opening it here is still opening it, though, so stamp
  // it: undoing after a read returns it read.
  //
  // Fires once per id per mount rather than once per id ever: this endpoint's
  // payload carries no viewedAt (RejectionWithOpportunity has no such field), so
  // there is nothing to guard on the way the pending drawer guards on
  // `opportunity.viewedAt`. Fire-and-forget, same as that drawer — a failed
  // stamp costs one row that stays bold, which is the safe direction to fail.
  const stampedRef = useRef<Set<string>>(new Set());
  const openedId = open ? rejection?.opportunity.id : undefined;
  useEffect(() => {
    if (!openedId || stampedRef.current.has(openedId)) return;
    stampedRef.current.add(openedId);
    markOpportunityViewed(openedId).catch((error) => {
      // Released on failure, so closing and reopening tries again. Holding the
      // id would suppress the stamp for the rest of the mount: one dropped
      // request and an Undo hands the opportunity back UNREAD even though the
      // user sat and read it — which is the guarantee this effect exists to
      // keep. Failing unread is the safe direction; failing unread FOREVER is
      // not. The guard is still enough for the case it is there for (a
      // close/reopen after a stamp that worked).
      stampedRef.current.delete(openedId);
      console.error('Mark rejected opportunity viewed error:', error);
    });
  }, [openedId]);

  if (!rejection) return null;

  const { opportunity, company } = rejection;
  const rating = ratingConfig[opportunity.llmRating];
  const reasonConfig = rejectionReasonConfig[rejection.rejectionReason];
  const reasonLabel = rejection.rejectionReason === 'other' && rejection.rejectionReasonText
    ? rejection.rejectionReasonText
    : reasonConfig.label;

  return (
    <Drawer
      title={null}
      placement="right"
      width="min(560px, 94vw)"
      onClose={onClose}
      open={open}
      className={css.rejectedDrawer}
      footer={
        <div className={css.footer}>
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      {/* Header */}
      <div className={css.header}>
        <div className={css.titleRow}>
          <Title level={4} className={css.companyName}>
            {company.name}
          </Title>
        </div>
        {opportunity.summary?.jobTitle && (
          <Text className={css.jobTitle}>{opportunity.summary.jobTitle}</Text>
        )}
        <div className={css.tags}>
          <SourceTag source={opportunity.source} sourceSite={opportunity.sourceSite} />
          <Tag color={rating.color}>{rating.label}</Tag>
          <Tag color={reasonConfig.color}>{reasonLabel}</Tag>
          <span className={css.similarity}>
            {Math.round(opportunity.similarityScore * 100)}% match
          </span>
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        className={css.calloutBanner}
        message={`You rejected this opportunity on ${formatDate(rejection.rejectedAt)}.`}
      />

      <Divider />

      {/* Fit Assessment */}
      <section className={css.section}>
        <Title level={5}>Fit Assessment</Title>
        {renderBulletList(opportunity.llmExplanation, 'No detailed explanation available.')}
      </section>

      <Divider />

      <Collapse
        ghost
        className={css.detailsCollapse}
        items={[
          ...(opportunity.summary
            ? [
                {
                  key: 'summary',
                  label: 'Job Summary',
                  children: (
                    <section className={css.section}>
                      <Paragraph>{opportunity.summary.generalOverview}</Paragraph>

                      {opportunity.summary.keyResponsibilities && (
                        <div className={css.listSection}>
                          <Text strong>Key Responsibilities:</Text>
                          <ul>
                            {opportunity.summary.keyResponsibilities.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {opportunity.summary.requiredQualifications && (
                        <div className={css.listSection}>
                          <Text strong>Required Qualifications:</Text>
                          <ul>
                            {opportunity.summary.requiredQualifications.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </section>
                  ),
                },
              ]
            : []),
          {
            key: 'company',
            label: `About ${company.name}`,
            children: (
              <section className={css.section}>
                <Paragraph>{company.description}</Paragraph>
                <div className={css.links}>
                  {company.websiteUrl && (
                    <a href={company.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <GlobalOutlined /> Website
                    </a>
                  )}
                  {company.linkedinUrl && (
                    <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      <LinkedinOutlined /> LinkedIn
                    </a>
                  )}
                </div>
              </section>
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default RejectedOpportunityDrawer;
